import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FUEL_TYPES, TRANSMISSIONS, BODY_TYPES } from "@/lib/format";
import {
  Zap, LogOut, Plus, Pencil, Trash2, Loader2, Car as CarIcon, Mail, LineChart as LineChartIcon,
  MessageSquare, Coins, Megaphone, Scale, Globe, Menu, Wallet, LayoutDashboard, Users, Bot, Search,
} from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { AdminSiteContent } from "@/components/AdminSiteContent";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminReviewsPanel } from "@/components/AdminReviewsPanel";
import { AdminPendingCars } from "@/components/AdminPendingCars";
import { AdminPricingConfig } from "@/components/AdminPricingConfig";
import { AdminLegalPagesConfig } from "@/components/AdminLegalPagesConfig";
import { AdminAdRequests } from "@/components/AdminAdRequests";
import { AdminMetaAds } from "@/components/AdminMetaAds";
import { AdminSeoAi } from "@/components/AdminSeoAi";
import { AdminVouchers } from "@/components/AdminVouchers";
import { AdminSignupPromos } from "@/components/AdminSignupPromos";
import { AdminInviteEmails } from "@/components/AdminInviteEmails";
import { AdminSupportAnalytics } from "@/components/AdminSupportAnalytics";
import { CarCard } from "@/components/CarCard";
import { QuickView } from "@/components/QuickView";
import { AdminFinance } from "@/components/AdminFinance";
import { AdminOverview } from "@/components/AdminOverview";
import { AdminUsers } from "@/components/AdminUsers";
import { AdminListingSupport } from "@/components/AdminListingSupport";
import { AdminAssistant } from "@/components/AdminAssistant";

const EMPTY = {
  make: "", model: "", year: 2022, price: 8000000, fuel: "Benzin", transmission: "Manuális",
  engine: "", horsepower: 150, acceleration: 9.0, mileage: 50000, body_type: "Ferdehátú", image: "", images: [], description: "",
};

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const { refresh: refreshSiteContent } = useSiteContent();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("overview");
  const [carsTab, setCarsTab] = useState("inventory");
  const [listingSupportTab, setListingSupportTab] = useState("viewings");
  const [quickViewCar, setQuickViewCar] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  const MORE_SECTIONS = new Set(["legal", "reviews", "ads", "seo", "support", "users", "listing-support"]);
  const moreMenuActive = MORE_SECTIONS.has(section);

  const adminNavigate = (target, sub) => {
    setSection(target);
    if (target === "cars" && sub === "pending") setCarsTab("pending");
    if (target === "listing-support") setListingSupportTab(sub === "ai" ? "ai" : "viewings");
  };

  const navBtn = (active) =>
    cn(
      "inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
      active ? "bg-electric text-white" : "text-slate-300 hover:bg-white/[0.06]",
    );

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/admin/login"); return; }
    if (user.role !== "admin") {
      toast.error("Ehhez az oldalhoz admin jogosultság szükséges. Jelentkezz be az admin fiókoddal.");
      logout();
      navigate("/admin/login");
    }
  }, [authLoading, user]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([api.get("/admin/cars"), api.get("/inquiries")]);
      setCars(c.data);
      setInquiries(i.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (car) => {
    setEditing(car);
    setForm(car);
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year),
      price: Number(form.price),
      horsepower: Number(form.horsepower),
      acceleration: Number(form.acceleration),
      mileage: Number(form.mileage),
    };
    try {
      if (editing) {
        await api.put(`/cars/${editing.id}`, payload);
        toast.success("Autó frissítve");
      } else {
        await api.post("/cars", payload);
        toast.success("Autó hozzáadva");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error("Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (car) => {
    if (!window.confirm(`Biztosan törlöd: ${car.make} ${car.model}?`)) return;
    try {
      await api.delete(`/cars/${car.id}`);
      toast.success("Autó törölve");
      load();
    } catch (e) {
      toast.error("Törlés sikertelen");
    }
  };

  const removeInquiry = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      load();
    } catch (e) {
      toast.error("Törlés sikertelen");
    }
  };

  if (authLoading || !user) {
    return (
      <div className="dark min-h-screen bg-anthracite-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <header className="glass-strong border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-electric" strokeWidth={1.5} />
            <span className="font-heading text-xl font-semibold">Auto<span className="text-electric">Park</span></span>
            <span className="text-xs text-slate-500 ml-2">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 hidden sm:inline">{user.email}</span>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <LogOut className="w-4 h-4" /> Kilépés
            </button>
          </div>
        </div>
      </header>

      <main className={cn("mx-auto px-6 py-10", section === "cars" ? "max-w-[2400px]" : "max-w-7xl")}>
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2">
          <div className="flex flex-1 flex-wrap items-center gap-1.5 min-w-0">
            <button type="button" data-testid="tab-overview" onClick={() => setSection("overview")} className={navBtn(section === "overview")}>
              <LayoutDashboard className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Áttekintő
            </button>
            <button type="button" data-testid="tab-assistant" onClick={() => setSection("assistant")} className={navBtn(section === "assistant")}>
              <Bot className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Asszisztens
            </button>
            <button type="button" data-testid="tab-seo-ai" onClick={() => setSection("seo")} className={navBtn(section === "seo")}>
              <Search className="h-4 w-4 shrink-0" strokeWidth={1.5} /> SEO AI
            </button>
            <button type="button" data-testid="tab-website" onClick={() => setSection("website")} className={navBtn(section === "website")}>
              <Globe className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Weboldal
            </button>
            <button type="button" data-testid="tab-pricing" onClick={() => setSection("pricing")} className={navBtn(section === "pricing")}>
              <Coins className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Árazás
            </button>
            <button type="button" data-testid="tab-cars" onClick={() => setSection("cars")} className={navBtn(section === "cars")}>
              <CarIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Autók ({cars.length})
            </button>
            <button type="button" data-testid="tab-inquiries" onClick={() => setSection("inquiries")} className={navBtn(section === "inquiries")}>
              <Mail className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Beérkező levelek ({inquiries.length})
            </button>
            <button type="button" data-testid="tab-finance" onClick={() => setSection("finance")} className={navBtn(section === "finance")}>
              <Wallet className="h-4 w-4 shrink-0" strokeWidth={1.5} /> Pénzügyek
            </button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="admin-more-menu"
                className={cn(navBtn(moreMenuActive), "ml-auto shrink-0")}
              >
                <Menu className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <span className="hidden sm:inline">Több</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-anthracite-800 border-white/10 text-white min-w-[220px]">
              <DropdownMenuItem
                data-testid="tab-users"
                className="focus:bg-electric focus:text-white cursor-pointer"
                onSelect={() => setSection("users")}
              >
                <Users className="h-4 w-4 mr-2" strokeWidth={1.5} /> Eladók
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tab-listing-support"
                className="focus:bg-electric focus:text-white cursor-pointer"
                onSelect={() => setSection("listing-support")}
              >
                <Bot className="h-4 w-4 mr-2" strokeWidth={1.5} /> AI & időpontok
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tab-legal"
                className="focus:bg-electric focus:text-white cursor-pointer"
                onSelect={() => setSection("legal")}
              >
                <Scale className="h-4 w-4 mr-2" strokeWidth={1.5} /> Jogi oldalak
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tab-reviews"
                className="focus:bg-electric focus:text-white cursor-pointer"
                onSelect={() => setSection("reviews")}
              >
                <MessageSquare className="h-4 w-4 mr-2" strokeWidth={1.5} /> Vélemények
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tab-ads"
                className="focus:bg-electric focus:text-white cursor-pointer"
                onSelect={() => setSection("ads")}
              >
                <Megaphone className="h-4 w-4 mr-2" strokeWidth={1.5} /> Meta hirdetés AI
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="tab-support"
                className="focus:bg-electric focus:text-white cursor-pointer"
                onSelect={() => setSection("support")}
              >
                <LineChartIcon className="h-4 w-4 mr-2" strokeWidth={1.5} /> Chat analitika
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {section === "overview" && (
          <div className="mt-6">
            <AdminOverview onNavigate={adminNavigate} />
          </div>
        )}

        {section === "assistant" && (
          <div className="mt-6">
            <AdminAssistant
              onMutate={load}
              onSiteContentSaved={() => refreshSiteContent().catch(() => {})}
            />
          </div>
        )}

        {section === "seo" && (
          <div className="mt-6">
            <AdminSeoAi />
          </div>
        )}

        {section === "website" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Weboldal — tartalom és struktúra</h2>
            <AdminSiteContent />
          </div>
        )}

        {section === "pricing" && (
          <Tabs defaultValue="pricing-main" className="mt-6">
            <TabsList className="flex h-auto w-full flex-wrap gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-1.5 mb-6">
              <TabsTrigger value="pricing-main" data-testid="tab-pricing-main" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
                Kreditár és csomagok
              </TabsTrigger>
              <TabsTrigger value="vouchers" data-testid="tab-vouchers" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
                Kuponok
              </TabsTrigger>
              <TabsTrigger value="signup-promos" data-testid="tab-signup-promos" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
                Reg. link
              </TabsTrigger>
              <TabsTrigger value="invite-emails" data-testid="tab-invite-emails" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
                Meghívók
              </TabsTrigger>
            </TabsList>
            <TabsContent value="pricing-main">
              <h2 className="font-heading text-2xl font-light mb-6">Árazás beállítások</h2>
              <AdminPricingConfig />
            </TabsContent>
            <TabsContent value="vouchers">
              <h2 className="font-heading text-2xl font-light mb-6">Kredit kuponok</h2>
              <AdminVouchers />
            </TabsContent>
            <TabsContent value="signup-promos">
              <h2 className="font-heading text-2xl font-light mb-6">Regisztrációs promó linkek</h2>
              <AdminSignupPromos />
            </TabsContent>
            <TabsContent value="invite-emails">
              <h2 className="font-heading text-2xl font-light mb-6">Meghívó emailek</h2>
              <AdminInviteEmails />
            </TabsContent>
          </Tabs>
        )}

        {section === "cars" && (
          <Tabs value={carsTab} onValueChange={setCarsTab} className="mt-6">
            <TabsList className="flex h-auto w-full flex-wrap gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-1.5 mb-6">
              <TabsTrigger value="inventory" data-testid="tab-cars-inventory" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
                Készlet
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-lg px-3 py-2 text-sm data-[state=active]:bg-electric data-[state=active]:text-white">
                Jóváhagyás
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inventory">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-2xl font-light">Készlet kezelése</h2>
                <button
                  data-testid="add-car-btn"
                  onClick={openNew}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light text-white text-sm font-medium transition-all"
                >
                  <Plus className="w-4 h-4" /> Új autó
                </button>
              </div>
              {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-electric animate-spin" /></div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
                  {cars.map((car) => (
                    <div key={car.id} data-testid={`admin-car-${car.id}`} className="flex flex-col gap-2 min-w-0">
                      <CarCard
                        compact
                        car={car}
                        onQuickView={(c) => {
                          setQuickViewCar(c);
                          setQuickViewOpen(true);
                        }}
                      />
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          data-testid={`edit-car-${car.id}`}
                          onClick={() => openEdit(car)}
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] glass transition-all hover:bg-white/10"
                        >
                          <Pencil className="w-3 h-3" /> Szerkeszt
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-car-${car.id}`}
                          onClick={() => remove(car)}
                          className="px-2 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                          aria-label="Törlés"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="pending">
              <h2 className="font-heading text-2xl font-light mb-6">Feltöltött hirdetések – Jóváhagyás</h2>
              <AdminPendingCars />
            </TabsContent>
          </Tabs>
        )}

        {section === "inquiries" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Beérkező levelek</h2>
            {inquiries.length === 0 ? (
              <p className="text-slate-400">Még nincs üzenet.</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <div key={inq.id} data-testid={`inquiry-${inq.id}`} className="glass rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium">{inq.name}</div>
                        <div className="text-sm text-slate-400">{inq.email} · {inq.phone || "nincs telefon"}</div>
                        {inq.car_label && (
                          <div className="inline-block mt-2 px-3 py-1 rounded-full bg-electric/15 text-electric text-xs">
                            {inq.car_label}
                          </div>
                        )}
                        <p className="text-sm text-slate-300 mt-2">{inq.message}</p>
                        <div className="text-xs text-slate-500 mt-2">
                          {new Date(inq.created_at).toLocaleString("hu-HU")}
                        </div>
                      </div>
                      <button
                        onClick={() => removeInquiry(inq.id)}
                        className="px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {section === "finance" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Pénzügyek</h2>
            <AdminFinance />
          </div>
        )}

        {section === "users" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Eladók és felhasználók</h2>
            <AdminUsers />
          </div>
        )}

        {section === "listing-support" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Listing AI & megtekintések</h2>
            <AdminListingSupport key={listingSupportTab} defaultTab={listingSupportTab} />
          </div>
        )}

        {section === "legal" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">ÁSZF és adatvédelmi dokumentumok</h2>
            <AdminLegalPagesConfig />
          </div>
        )}

        {section === "reviews" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Vélemény moderáció</h2>
            <AdminReviewsPanel />
          </div>
        )}

        {section === "ads" && (
          <div className="mt-6 space-y-10">
            <AdminMetaAds />
            <div>
              <h2 className="font-heading text-xl font-light mb-4 text-slate-400">Egyéb megosztási kérések</h2>
              <AdminAdRequests />
            </div>
          </div>
        )}

        {section === "support" && (
          <div className="mt-6">
            <h2 className="font-heading text-2xl font-light mb-6">Ügyfélszolgálati chatbot analitika</h2>
            <AdminSupportAnalytics />
          </div>
        )}
      </main>

      <QuickView
        car={quickViewCar}
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
      />

      {section !== "assistant" && (
        <button
          type="button"
          data-testid="admin-assistant-fab"
          title="Admin asszisztens"
          onClick={() => setSection("assistant")}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-electric text-white shadow-lg shadow-electric/30 hover:scale-105 transition-transform"
        >
          <Bot className="h-6 w-6" strokeWidth={1.5} />
        </button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-anthracite-800 border border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              {editing ? "Autó szerkesztése" : "Új autó hozzáadása"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Inp label="Márka" testid="form-make" value={form.make} onChange={(v) => setForm({ ...form, make: v })} required />
            <Inp label="Modell" testid="form-model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} required />
            <Inp label="Évjárat" testid="form-year" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} required />
            <Inp label="Ár (Ft)" testid="form-price" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required />
            <Sel label="Üzemanyag" testid="form-fuel" value={form.fuel} onChange={(v) => setForm({ ...form, fuel: v })} options={FUEL_TYPES} />
            <Sel label="Váltó" testid="form-transmission" value={form.transmission} onChange={(v) => setForm({ ...form, transmission: v })} options={TRANSMISSIONS} />
            <Inp label="Motor" testid="form-engine" value={form.engine} onChange={(v) => setForm({ ...form, engine: v })} required />
            <Inp label="Teljesítmény (LE)" testid="form-hp" type="number" value={form.horsepower} onChange={(v) => setForm({ ...form, horsepower: v })} required />
            <Inp label="Gyorsulás (0-100 mp)" testid="form-acc" type="number" step="0.1" value={form.acceleration} onChange={(v) => setForm({ ...form, acceleration: v })} required />
            <Inp label="Futott km" testid="form-mileage" type="number" value={form.mileage} onChange={(v) => setForm({ ...form, mileage: v })} required />
            <Sel label="Karosszéria" testid="form-body" value={form.body_type} onChange={(v) => setForm({ ...form, body_type: v })} options={BODY_TYPES} />
            <Inp label="Fő kép URL" testid="form-image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} required />
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">
                További fotók / szögek (soronként egy URL)
              </label>
              <textarea
                data-testid="form-images"
                rows={3}
                value={(form.images || []).join("\n")}
                onChange={(e) => setForm({ ...form, images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                placeholder="https://.../belso.jpg&#10;https://.../muszerfal.jpg&#10;https://.../kerek.jpg"
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-slate-500 focus:border-electric outline-none transition-colors resize-none"
              />
              <p className="text-xs text-slate-500 mt-1">A galéria a fő képpel kezdődik, majd az itt megadott képek következnek.</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">Leírás</label>
              <textarea
                data-testid="form-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white focus:border-electric outline-none transition-colors resize-none"
              />
            </div>
            <div className="md:col-span-2">
              <button
                data-testid="form-submit"
                type="submit"
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? "Mentés" : "Hozzáadás"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Inp = ({ label, testid, value, onChange, type = "text", required, step }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      step={step}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm text-white focus:border-electric outline-none transition-colors"
    />
  </div>
);

const Sel = ({ label, testid, value, onChange, options }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white/[0.03] border-white/10 text-white h-11">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-anthracite-700 border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="focus:bg-electric focus:text-white">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
